// ─────────────────────────────────────────────────────────────────────────────
// Welcome — the first page of the Guide
//
// Ports the existing GettingStarted.js content into the Guide structure.
// Same founder voice, same honest framing of the 30-day trial path, but
// now lives inside the Guide instead of as a separate sidebar entry, and
// links forward to the deeper pages (Dashboard, Trade Entry, etc) for the
// reader who wants more after orientation.
// ─────────────────────────────────────────────────────────────────────────────

import { Link } from "react-router-dom";
import {
  GuideLayout, H2, P, UL, LI, Field, UI, Callout,
} from "../GuideLayout";
import { C } from "../../../lib/colors";

const SUPPORT_EMAIL = "admin@futures-os.app";

// Small inline component for the trade-count milestone rows
function MilestoneRow({ count, label, body }) {
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "100px 1fr",
      gap: 16,
      padding: "12px 0",
      borderBottom: `1px solid ${C.border}40`,
    }}>
      <div>
        <div style={{ fontSize: 17, fontWeight: 800, color: C.green, fontFamily: "monospace" }}>
          {count}
        </div>
        <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.1em" }}>
          {label}
        </div>
      </div>
      <div style={{ fontSize: 13.5, color: C.sub, lineHeight: 1.65 }}>
        {body}
      </div>
    </div>
  );
}

export function GuideWelcome() {
  return (
    <GuideLayout
      title="Welcome to Futures OS"
      subtitle="What this product is, how to use it, and what to expect across your trial."
      next={{ slug: "dashboard", label: "Dashboard" }}
    >
      <P>
        Welcome. I'm Michael, and I built Futures OS because the existing
        trade journals don't do what I needed: tell me <em>what I'm doing
        wrong</em>, not just <em>what I did</em>.
      </P>
      <P>
        Here's the honest version of how to get the most out of your trial.
      </P>

      <H2>What this product is, and isn't</H2>
      <P>
        Futures OS isn't going to predict markets or tell you what to trade.
        It looks at your actual trading history and surfaces patterns you
        can't see in P&L alone — when you size up after losses, which time
        slots you bleed in, whether your manual exits beat your planned
        ones, what mental states cost you money.
      </P>
      <P>
        It does this with 22 detectors that run on your trade data. The
        detectors need data to fire. The product gets meaningfully better
        the more you log.
      </P>

      <H2>Days 1–2: Set up your account</H2>
      <P>
        When you first sign in, an onboarding wizard walks you through
        creating your first account and choosing how you'll log trades.
        If you skip the wizard, go to <UI>Portfolio</UI> and add your
        combine or funded account manually. Either way, set your prop firm,
        account type, and starting balance — and set your max daily drawdown
        in <UI>Trading Rules</UI> to match your prop firm's rule so Futures
        OS can track your real exposure each session.
      </P>

      <H2>Days 1–7: Get your trades in</H2>
      <P>Two ways:</P>
      <UL>
        <LI>
          <UI>Log each trade once it's closed</UI> in Trade Entry. Fastest
          path to value because the fields that unlock detectors — setup
          type, mental state, exit reason — are easiest to capture while
          the trade is still fresh in your head.
        </LI>
        <LI>
          <UI>Import history from your broker.</UI> TopStep and Tradovate's
          Performance export work best — they bring in the trade mechanics
          (symbol, prices, size, P&L, time) cleanly. Other brokers and the
          Tradovate Orders export work too, but may need some fields filled
          in after. Note that no broker export includes the behavioral
          fields, so imported trades will show fewer detector results until
          you add those manually. Email me if you hit anything and I'll
          help.
        </LI>
      </UL>
      <P>
        Whether that takes a week or a month depends on your trading
        cadence. As a rough benchmark, the first detectors start firing
        around 15–20 logged trades — so however long that takes you,
        that's when the product starts showing you something.
      </P>

      <H2>The fields that matter</H2>
      <P>
        You can log a trade with just the basics (symbol, entry, exit,
        size). But the detectors that catch behavioral patterns need more:
      </P>
      <UL>
        <LI><Field>stop_loss</Field> — unlocks Stop Adherence, R-Multiple Distribution, Stop Widening</LI>
        <LI><Field>setup_type</Field> — unlocks Setup Edge Ranking</LI>
        <LI><Field>mental_state</Field> — unlocks the Psychology suite</LI>
        <LI><Field>market_condition</Field> + <Field>is_news_event</Field> — unlocks Context detectors</LI>
      </UL>
      <P>
        You don't need to fill these in for every trade. But the more you
        do, the more the product can see.
      </P>

      <H2>What to expect, by trade count</H2>
      <P>
        Each detector card shows its progress — how many samples it has,
        how many it needs for a confident read. Some fire at 15 trades.
        Others need 30. You'll see "early signal" hedges on detectors with
        partial data: useful, but not yet definitive.
      </P>

      <div style={{ marginTop: 14, marginBottom: 18 }}>
        <MilestoneRow
          count="15–30"
          label="trades"
          body="First detectors fire. You'll likely see something surprising — most traders do."
        />
        <MilestoneRow
          count="30–50"
          label="trades"
          body="Behavior and Risk detectors start producing reliable reads. AI Insights starts synthesizing across suites."
        />
        <MilestoneRow
          count="100+"
          label="trades"
          body="Full picture. Pattern detection across multiple dimensions. This is when the product earns its keep."
        />
      </div>

      <P>
        How long that takes depends on your trading style. Higher-frequency
        traders reach 100 logged trades within weeks; swing or selective
        traders may take the full 30 days or longer to get there. Neither
        pace is right or wrong — the product just gets sharper as your log
        grows. Trade your edge, not the journal.
      </P>

      <H2>About your trial</H2>
      <P>
        You have 30 days of full access starting from when you sign up —
        no credit card required to start. After 30 days it's $69/month
        or $599/year. Three days before your trial ends you'll see a
        banner in the app prompting you to add a card. If no card is
        added by day 30, the trial ends and the account is paused — your
        data stays intact and you can resubscribe anytime.
      </P>

      <H2>Around the app</H2>
      <P>
        A quick map so you know what's where. The rest of this Guide
        covers each surface in more depth — click any name below to jump
        straight to that page.
      </P>

      <UL>
        <LI><Link to="/guide/dashboard"     style={{ color: C.blue }}>Dashboard</Link> — your home screen, today's plan, drawdown room</LI>
        <LI><Link to="/guide/trade-entry"   style={{ color: C.blue }}>Trade Entry</Link> — log closed trades, fields that matter</LI>
        <LI><Link to="/guide/can-i-trade"   style={{ color: C.blue }}>Can I Trade?</Link> — preflight checklist before a new order</LI>
        <LI><Link to="/guide/trade-review"  style={{ color: C.blue }}>Trade Review</Link> — coaching feedback on a single trade</LI>
        <LI><Link to="/guide/journal"       style={{ color: C.blue }}>Journal</Link> — your full trade history</LI>
        <LI><Link to="/guide/analytics"     style={{ color: C.blue }}>Analytics</Link> — detector suites and how to read them</LI>
        <LI><Link to="/guide/eval-survival" style={{ color: C.blue }}>Eval Survival</Link> — fit your history against prop firm rules</LI>
        <LI><Link to="/guide/portfolio"     style={{ color: C.blue }}>Portfolio & Trading Rules</Link> — accounts and the rules each one trades by</LI>
        <LI><Link to="/guide/market"        style={{ color: C.blue }}>Market</Link> — economic calendar and pre-market brief</LI>
      </UL>

      <H2>When you need help</H2>
      <P>
        Email{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}?subject=Futures%20OS%20support`}
          style={{ color: C.green, textDecoration: "underline" }}
        >
          {SUPPORT_EMAIL}
        </a>
        . I read every one. Bug reports, feature requests, "this isn't
        working how I expected" — all welcome. The product is early and your
        feedback shapes it.
      </P>

      <Callout kind="tip" title="Ready to go deeper?">
        Click <strong>Next →</strong> at the bottom of this page to start
        the walkthrough proper, beginning with the Dashboard. Or jump
        straight to whatever surface you're trying to learn from the list
        above.
      </Callout>
    </GuideLayout>
  );
}



